import { AudioPreset } from '../utils/audio-optimizer';

import { RNNoiseProcessor } from './NoiseCancellation/RNNoiseProcessor';
import { DeepFilterProcessor } from './NoiseCancellation/DeepFilterProcessor';

import { DEFAULT_SUPPRESSION_LEVEL, INoiseProcessor, AudioQualityMetrics } from './NoiseCancellation/types';

export { type AudioQualityMetrics };

export class AudioProcessorService {
  private audioContext: AudioContext | null = null;
  private activeProcessor: INoiseProcessor | null = null;

  // State
  private _currentPreset: AudioPreset = 'high';
  private _smartMixingEnabled: boolean = false;
  private _dynamicSuppressionEnabled: boolean = true;
  private _suppressionLevel: number = DEFAULT_SUPPRESSION_LEVEL;
  private _echoCancellationEnabled: boolean = true; // Default ON

  // Metrics tracking
  private qualityMetrics: AudioQualityMetrics = {};
  private metricsCallbacks: Set<(metrics: AudioQualityMetrics) => void> = new Set();

  private isMuted: boolean = false;

  constructor() {
    this._currentPreset = 'high';
  }

  public get node(): AudioWorkletNode | null {
    return this.activeProcessor?.node || null;
  }

  public get currentPreset(): AudioPreset {
    return this._currentPreset;
  }

  public get smartMixingEnabled(): boolean {
    return this._smartMixingEnabled;
  }

  public get dynamicSuppressionEnabled(): boolean {
    return this._dynamicSuppressionEnabled;
  }

  public get suppressionLevel(): number {
    return this._suppressionLevel;
  }

  public get echoCancellationEnabled(): boolean {
    return this._echoCancellationEnabled;
  }

  public getAudioContext(): AudioContext {
    if (!this.audioContext || this.audioContext.state === 'closed') {
      const t0 = performance.now();
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        latencyHint: 'interactive', // Optimized for real-time communication
        sampleRate: 48000,
      });
      console.log(
        `[AudioProcessor] Context created in ${(performance.now() - t0).toFixed(1)}ms ` +
          `| sampleRate=${this.audioContext.sampleRate} ` +
          `| baseLatency=${(this.audioContext.baseLatency * 1000).toFixed(1)}ms`
      );
    }
    return this.audioContext;
  }

  public async processStream(stream: MediaStream): Promise<MediaStream> {
    try {
      // Reuse existing processor if it matches the requested preset (High/DeepFilter)
      if (
        this.activeProcessor &&
        this._currentPreset === 'high' &&
        this.activeProcessor instanceof DeepFilterProcessor
      ) {
        console.log('[AudioProcessor] Reusing existing DeepFilter instance');
        // Re-initialize or Re-route
        // DeepFilterProcessor.process() handles re-routing internally
        const processedStream = await this.activeProcessor.process(stream);
        this.emitMetrics();
        return processedStream;
      }

      // Stop any existing processing to cleanup resources/nodes if we are NOT reusing
      this.stop();

      // Instantiate appropriate processor
      // NOTE: We recreate the processor instance on stream change to ensure clean state
      if (this._currentPreset === 'high') {
        this.activeProcessor = new DeepFilterProcessor();
      } else {
        this.activeProcessor = new RNNoiseProcessor();
      }

      const ctx = this.getAudioContext();
      if (ctx.state === 'suspended') {
        await ctx.resume();
      }

      // Initialize with context
      if (!this.activeProcessor) return stream;
      await this.activeProcessor.init(ctx);

      // sync initial smart mixing state
      this.activeProcessor.setSmartMixing(this._smartMixingEnabled);
      if (this.activeProcessor.setSuppressionLevel) {
        this.activeProcessor.setSuppressionLevel(this._suppressionLevel);
      }

      // Sync dynamic suppression state
      if (this.activeProcessor.setDynamicSuppression) {
        this.activeProcessor.setDynamicSuppression(this._dynamicSuppressionEnabled);
      }

      // Process stream
      const processedStream = await this.activeProcessor.process(stream);

      this.activeProcessor.onMetrics((metrics) => {
        // Merge partial metrics into our state
        this.qualityMetrics = { ...this.qualityMetrics, ...metrics };
        this.emitMetrics();
      });

      // Update initial state
      this.isMuted = false;
      this.emitMetrics();

      console.log(`[AudioProcessor] Processing started with preset: ${this._currentPreset}`);
      return processedStream;
    } catch (error) {
      console.error('[AudioProcessor] Setup failed, returning original stream', error);
      this.stop(); // Ensure cleanup
      return stream;
    }
  }

  public setPreset(preset: AudioPreset) {
    if (this._currentPreset === preset) return;
    this._currentPreset = preset;
    this.emitMetrics();
    // Logic note: UI/Consumer must recall processStream() to apply changes
  }

  public async enableProcessing(enabled: boolean) {
    // Determine target state and sync
    if (!this.activeProcessor) return;
    try {
      await this.activeProcessor.enableProcessing(enabled);
    } catch (e) {
      console.warn('[AudioProcessor] enableProcessing failed', e);
    }

    // Also manage context state if mostly appropriate
    if (this.audioContext) {
      if (enabled && this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      } else if (!enabled && this.audioContext.state === 'running') {
        await this.audioContext.suspend();
      }
    }
  }

  public async setMuted(muted: boolean) {
    if (this.isMuted === muted) return;
    this.isMuted = muted;
    this.emitMetrics();

    if (this.activeProcessor) {
      try {
        await this.activeProcessor.setMuted(muted);
      } catch (e) {
        console.warn('[AudioProcessor] setMuted failed', e);
      }
    }
  }

  public setSmartMixing(enabled: boolean) {
    this._smartMixingEnabled = enabled;
    if (!this.activeProcessor) return;
    try {
      this.activeProcessor.setSmartMixing(enabled);
      console.log(`[AudioProcessor] Smart Mixing ${enabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      console.warn('[AudioProcessor] setSmartMixing failed', e);
    }
  }

  public setDynamicSuppression(enabled: boolean) {
    this._dynamicSuppressionEnabled = enabled;
    if (!this.activeProcessor || !this.activeProcessor.setDynamicSuppression) return;
    try {
      this.activeProcessor.setDynamicSuppression(enabled);
      console.log(`[AudioProcessor] Dynamic Suppression ${enabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      console.warn('[AudioProcessor] setDynamicSuppression failed', e);
    }
  }

  public setSuppressionLevel(level: number) {
    this._suppressionLevel = level;
    if (!this.activeProcessor || !this.activeProcessor.setSuppressionLevel) return;
    try {
      this.activeProcessor.setSuppressionLevel(level);
      console.log(`[AudioProcessor] Suppression Level set to ${level}`);
    } catch (e) {
      console.warn('[AudioProcessor] setSuppressionLevel failed', e);
    }
  }

  /**
   * Toggle browser echo cancellation.
   * Requires audio producer restart (atomicCloseThenProduce) to take effect
   * since constraints are applied at getUserMedia time.
   */
  public setEchoCancellation(enabled: boolean) {
    this._echoCancellationEnabled = enabled;
    console.log(`[AudioProcessor] Echo Cancellation ${enabled ? 'enabled' : 'disabled'}`);
  }

  // Smart Mixing Component Toggles
  public setPreGainEnabled(enabled: boolean) {
    if (this.activeProcessor?.setPreGainEnabled) this.activeProcessor.setPreGainEnabled(enabled);
  }
  public setEQEnabled(enabled: boolean) {
    if (this.activeProcessor?.setEQEnabled) this.activeProcessor.setEQEnabled(enabled);
  }
  public setCompressorEnabled(enabled: boolean) {
    if (this.activeProcessor?.setCompressorEnabled) this.activeProcessor.setCompressorEnabled(enabled);
  }
  public setGateEnabled(enabled: boolean) {
    if (this.activeProcessor?.setGateEnabled) this.activeProcessor.setGateEnabled(enabled);
  }
  public setPostGainEnabled(enabled: boolean) {
    if (this.activeProcessor?.setPostGainEnabled) this.activeProcessor.setPostGainEnabled(enabled);
  }
  public getSmartMixingOptions() {
    return this.activeProcessor?.getSmartMixingOptions ? this.activeProcessor.getSmartMixingOptions() : undefined;
  }

  private emitMetrics() {
    this.metricsCallbacks.forEach((cb) => cb({ ...this.qualityMetrics }));
  }

  public onMetrics(callback: (metrics: AudioQualityMetrics) => void) {
    this.metricsCallbacks.add(callback);
    return () => {
      this.metricsCallbacks.delete(callback);
    };
  }

  public getMetrics(): AudioQualityMetrics {
    return { ...this.qualityMetrics };
  }

  public stop() {
    if (this.activeProcessor) {
      this.activeProcessor.destroy();
      this.activeProcessor = null;
    }
  }

  /**
   * Full teardown — closes the AudioContext.
   * Only call when the service is truly being destroyed (e.g., leaving a room).
   */
  public dispose() {
    this.stop();
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close().catch((e) => console.warn('[AudioProcessor] Error closing context', e));
      this.audioContext = null;
    }
  }
}

export const audioProcessor = new AudioProcessorService();
